/**
 * AI Search Ads Module
 * Handles dynamic ad display logic for AI search page using unified ad library
 *
 * Ad 1: Shows immediately when query is submitted, removed when answer starts streaming
 * Ad 2: Shows after answer completes, below references or rating section (lazy-loaded)
 * 
 * MEDAIS-2095: Batched ad calls - both ads requested in single network call
 * MEDAIS-2398: Migrated from webmd.ads2 to unified-ad-library (<ad-unit> custom elements)
 * Desktop Ad positions: 101 (Ad 1), 141 (Ad 2)
 * Mobile Ad positions: 1122 (Ad 1), 1520 (Ad 2)
 */

var _vars = require('./vars')._vars;

// Ad configuration
var AD_CONFIG = {
    ad1: {
        desktop: 101,
        mobile: 1122,
        containerId: 'ai-search-ad1-container'
    },
    ad2: {
        desktop: 141,
        mobile: 1520,  // MEDAIS-2095: Changed from 1122 to 1520 for mobile
        containerId: 'ai-search-ad2-container'
    }
};

// Track active ads for cleanup
var activeAds = {
    ad1: null,
    ad1Counter: 0,  // Counter for sequential Ad1 naming
    ad2: null,  // MEDAIS-2095: Changed from array to single object for batched approach
    ad2Counter: 0  // Counter for sequential Ad2 naming
};

function isMobile() {
    return window.innerWidth <= 768;
}

function hasValidUrlScg() {
    try {
        var params = new URLSearchParams(window.location.search);
        
        // PDQ requires BOTH query AND scg params to be present in the URL.
        // Having only scg without query is not a PDQ flow.
        var queryParam = params.get('query');
        if (!queryParam || queryParam.trim() === '') {
            return false;
        }
        
        var scgParam = params.get('scg');        
        
        if (!scgParam) {
            return false;
        }
        
        var trimmed = scgParam.trim();
        // Remove quotes if present
        if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
            (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
            trimmed = trimmed.slice(1, -1).trim();
        }
        
        
        // Validate value
        if (trimmed !== '' && 
            trimmed.length > 0 &&
            !/^\s*$/.test(trimmed) &&
            trimmed.toLowerCase() !== 'null' && 
            trimmed.toLowerCase() !== 'none' && 
            trimmed.toLowerCase() !== 'undefined' &&
            trimmed !== '0' &&  // Reject string "0"
            /^\d+$/.test(trimmed)) {  // Must be numeric (digits only)
            return true;
        }
        
        return false;
    } catch (error) {
        console.error('[AD VALIDATION] Error checking URL scg:', error);
        return false;
    }
}

/**
 * Checks if _vars.leadConcept has a valid value (concept_id)
 * For subsequent queries after PDQ, leadConcept may have source='url' but still be valid
 */
function hasValidApiConceptId() {
    
    if (!_vars.leadConcept || !_vars.leadConcept.value) {
        return false;
    }
    
    var value = _vars.leadConcept.value;
    
    if (typeof value !== 'string') {
        return false;
    }
    
    var trimmed = value.trim();
    
    // Validate value
    if (trimmed !== '' && 
        trimmed.length > 0 &&
        !/^\s*$/.test(trimmed) &&
        trimmed.toLowerCase() !== 'null' && 
        trimmed.toLowerCase() !== 'none' && 
        trimmed.toLowerCase() !== 'undefined' &&
        trimmed !== '0' &&  // Reject string "0"
        /^\d+$/.test(trimmed)) {  // Must be numeric (digits only)
        return true;
    }
    
    return false;
}

/**
 * Checks if PageMetadata.pageSegVars.scg exists
 */
function hasPageMetadataScg() {
    try {
        if (typeof PageMetadata !== 'undefined' && 
            PageMetadata.pageSegVars && 
            PageMetadata.pageSegVars.scg) {
            return true;
        }
        return false;
    } catch (error) {
        console.error('[AD] Error checking PageMetadata:', error);
        return false;
    }
}

/**
 * MEDAIS-2095: Creates batched ad call for both Ad 1 and Ad 2 in a single network request
 * MEDAIS-2398: Uses <ad-unit> custom elements from unified ad library
 * @returns {Object} Object containing both ad info objects {ad1Info, ad2Info}
 */
function createBatchedAdCall() {
    try {
        // Only show on full-width landing page
        if (!_vars.isFullPage) {
            return null;
        }
        
        // Check for PageMetadata.pageSegVars.scg
        if (!hasPageMetadataScg()) {
            return null;
        }

        // Increment counters
        activeAds.ad1Counter++;
        activeAds.ad2Counter++;

        // Set page targets before ad-unit elements are created
        if (window.adLib && window.adLib.gpt) {
            if (typeof s_pageview_id !== 'undefined') {
                window.adLib.gpt.setPageTarget('pvid', s_pageview_id);
            }
            if (_vars.queryId) {
                window.adLib.gpt.setPageTarget('qid', _vars.queryId);
            }
        }

        // Create Ad 1 container with desktop and mobile ad-unit elements
        var ad1Container = document.createElement('div');
        ad1Container.id = AD_CONFIG.ad1.containerId;
        ad1Container.className = 'ai-search-ad-container';

        var ad1Desktop = document.createElement('ad-unit');
        ad1Desktop.setAttribute('pos', AD_CONFIG.ad1.desktop);
        ad1Desktop.setAttribute('breakpoint', 'DESKTOP');
        ad1Desktop.className = 'AdUnit';
        ad1Container.appendChild(ad1Desktop);

        var ad1Mobile = document.createElement('ad-unit');
        ad1Mobile.setAttribute('pos', AD_CONFIG.ad1.mobile);
        ad1Mobile.setAttribute('breakpoint', 'MOBILE');
        ad1Mobile.className = 'AdUnit';
        ad1Container.appendChild(ad1Mobile);

        window.dispatchEvent(new CustomEvent('aiEvtAdApnd', {
            detail: {
                ad: 'pos-' + AD_CONFIG.ad1.desktop + '/' + AD_CONFIG.ad1.mobile,
                qnum: document.querySelectorAll('.ai-search-question').length
            }
        }));
        window.dispatchEvent(new CustomEvent('aiEvtAdDefined', {
            detail: {
                ad: 'pos-' + AD_CONFIG.ad1.desktop + '/' + AD_CONFIG.ad1.mobile,
                qnum: document.querySelectorAll('.ai-search-question').length
            }
        }));

        // Create Ad 2 container with desktop and mobile ad-unit elements (hidden initially)
        var ad2Container = document.createElement('div');
        ad2Container.id = AD_CONFIG.ad2.containerId + '-' + activeAds.ad2Counter;
        ad2Container.className = 'ai-search-ad-container';
        ad2Container.style.display = 'none';

        var ad2Desktop = document.createElement('ad-unit');
        ad2Desktop.setAttribute('pos', AD_CONFIG.ad2.desktop);
        ad2Desktop.setAttribute('breakpoint', 'DESKTOP');
        ad2Desktop.className = 'AdUnit';
        ad2Container.appendChild(ad2Desktop);

        var ad2Mobile = document.createElement('ad-unit');
        ad2Mobile.setAttribute('pos', AD_CONFIG.ad2.mobile);
        ad2Mobile.setAttribute('breakpoint', 'MOBILE');
        ad2Mobile.className = 'AdUnit';
        ad2Container.appendChild(ad2Mobile);

        window.dispatchEvent(new CustomEvent('aiEvtAdApnd', {
            detail: {
                ad: 'pos-' + AD_CONFIG.ad2.desktop + '/' + AD_CONFIG.ad2.mobile,
                qnum: document.querySelectorAll('.ai-search-question').length
            }
        }));
        window.dispatchEvent(new CustomEvent('aiEvtAdDefined', {
            detail: {
                ad: 'pos-' + AD_CONFIG.ad2.desktop + '/' + AD_CONFIG.ad2.mobile,
                qnum: document.querySelectorAll('.ai-search-question').length
            }
        }));
        window.dispatchEvent(new CustomEvent('aiEvtAdRefresh', {
            detail: {
                ad: 'pos-' + AD_CONFIG.ad1.desktop + '/' + AD_CONFIG.ad1.mobile + ',' + AD_CONFIG.ad2.desktop + '/' + AD_CONFIG.ad2.mobile,
                qnum: document.querySelectorAll('.ai-search-question').length
            }
        }));

        // Return ad management objects for both ads
        return {
            ad1Info: {
                container: ad1Container,
                destroy: function() {
                    try {
                        if (ad1Container && ad1Container.parentNode) {
                            ad1Container.parentNode.removeChild(ad1Container);
                        }
                        window.dispatchEvent(new CustomEvent('aiEvtAdDestroyed', {
                            detail: {
                                ad: 'pos-' + AD_CONFIG.ad1.desktop + '/' + AD_CONFIG.ad1.mobile,
                                qnum: document.querySelectorAll('.ai-search-question').length
                            }
                        }));
                    } catch (error) {
                        console.error('[AD] Error destroying Ad 1:', error);
                    }
                }
            },
            ad2Info: {
                container: ad2Container,
                destroy: function() {
                    try {
                        if (ad2Container && ad2Container.parentNode) {
                            ad2Container.parentNode.removeChild(ad2Container);
                        }
                        window.dispatchEvent(new CustomEvent('aiEvtAdDestroyed', {
                           detail: {
                                ad: 'pos-' + AD_CONFIG.ad2.desktop + '/' + AD_CONFIG.ad2.mobile,
                                qnum: document.querySelectorAll('.ai-search-question').length
                            }
                        }));
                    } catch (error) {
                        console.error('[AD] Error destroying Ad 2:', error);
                    }
                }
            }
        };
    } catch (error) {
        console.error('[AD] Error creating batched ad call:', error);
        return null;
    }
}

/**
 * MEDAIS-2095: Shows Ad 1 when query is submitted using batched ad call
 * Creates both Ad 1 and Ad 2 in a single network request, but only displays Ad 1 initially
 * @param {string} source - 'url' or 'api' to indicate validation source (parameter kept for compatibility)
 * @returns {boolean} True if ad was shown successfully
 */
function showAd1OnQuery(source) {
    try {
        // Create batched ad call for both Ad 1 and Ad 2
        var batchedAds = createBatchedAdCall();
        
        if (!batchedAds) {
            return false;
        }
        
        var ad1Info = batchedAds.ad1Info;
        var ad2Info = batchedAds.ad2Info;
        
        // Store Ad 2 info for later display
        activeAds.ad2 = ad2Info;
        
        // Helper function to scroll to ad (called only once after successful insertion)
        var scrollToAd = function() {
            if (_vars.isFullPage) {
                var scrollContainer = document.querySelector('.ai-search-content');
                if (scrollContainer) {
                    setTimeout(function() {
                        var adTop = ad1Info.container.offsetTop;
                        scrollContainer.scrollTo({
                            top: Math.max(0, adTop),
                            behavior: 'smooth'
                        });
                    }, 100);
                }
            }
        };
        
        // Use the stored reference to the question element from renderQuestion()
        // This avoids the timing issue where the question exists but can't be found via selector
        var currentQuestion = _vars.lastCreatedQuestion;
        
        if (currentQuestion && currentQuestion.parentNode) {
            // Question element exists and is in the DOM - insert ad directly above it
            currentQuestion.insertAdjacentElement('beforebegin', ad1Info.container);
            
            // PROFCORE-34567: Insert Ad 2 into the answer block that was created in renderQuestion
            var answerBlocks = document.querySelectorAll('.ai-search-answer.active');
            if (answerBlocks.length > 0) {
                var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
                var answerDiv = lastAnswerBlock.querySelector('.answer');
                if (answerDiv) {
                    answerDiv.appendChild(ad2Info.container);
                }
            }
            
            activeAds.ad1 = ad1Info;
            scrollToAd();
            return true;
        }
        
        // Fallback: if stored reference doesn't exist, try searching by selector
        currentQuestion = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
        
        if (currentQuestion) {
            currentQuestion.insertAdjacentElement('beforebegin', ad1Info.container);
            
            // PROFCORE-34567: Insert Ad 2 into the answer block that was created in renderQuestion
            var answerBlocks = document.querySelectorAll('.ai-search-answer.active');
            if (answerBlocks.length > 0) {
                var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
                var answerDiv = lastAnswerBlock.querySelector('.answer');
                if (answerDiv) {
                    answerDiv.appendChild(ad2Info.container);
                }
            }
            
            activeAds.ad1 = ad1Info;
            scrollToAd();
            return true;
        }
        
        // If no question found, cleanup both ads
        if (ad1Info.container && ad1Info.container.parentNode) {
            ad1Info.container.parentNode.removeChild(ad1Info.container);
        }
        if (ad2Info.container && ad2Info.container.parentNode) {
            ad2Info.container.parentNode.removeChild(ad2Info.container);
        }
        activeAds.ad2 = null;
        
        return false;
        
    } catch (error) {
        console.error('[AD] Error showing Ad 1:', error);
        return false;
    }
}


/**
 * MEDAIS-2095: Shows Ad 2 after answer completes
 * PROFCORE-34567: Simply makes Ad 2 visible without moving it to prevent iframe destruction
 */
function showAd2AfterAnswer() {
    try {
        // Safety check: only proceed for full-width page
        if (!_vars.isFullPage) {
            return false;
        }
        
        // Check if Ad 2 was created in the batched call
        if (!activeAds.ad2) {
            return false;
        }
        
        var ad2Info = activeAds.ad2;
        
        if (ad2Info.container) {
            // Simply make Ad 2 visible (don't move it to prevent iframe destruction)
            ad2Info.container.style.display = '';
            return true;
        }
        
        return false;
        
    } catch (error) {
        console.error('[AD] Error showing Ad 2:', error);
        return false;
    }
}

/**
 * Cleans up all active ads
 */
function cleanupAllAds() {
    try {
        // Remove Ad 1
        if (activeAds.ad1) {
            activeAds.ad1.destroy();
            activeAds.ad1 = null;
        }
        
        // Remove Ad 2 (MEDAIS-2095: changed from array to single object)
        if (activeAds.ad2) {
            activeAds.ad2.destroy();
            activeAds.ad2 = null;
        }
        
        // Reset counters
        activeAds.ad1Counter = 0;
        activeAds.ad2Counter = 0;
        
        // Fallback cleanup: remove all ad containers
        var allAd1Containers = document.querySelectorAll('[id^="ai-search-ad1-container"]');
        allAd1Containers.forEach(function(container) {
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
        });
        
        var allAd2Containers = document.querySelectorAll('[id^="ai-search-ad2-container"]');
        allAd2Containers.forEach(function(container) {
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
        });
    } catch (error) {
        console.error('[AD] Error cleaning up ads:', error);
    }
}


/**
 * PROFCORE-34567: Inserts Ad 2 into the answer block when it's first created
 * Called by renderAnswer in shared.js when answer block is created
 */
function insertAd2IntoAnswerBlock() {
    try {
        // Only proceed if Ad 2 exists and hasn't been inserted yet
        if (!activeAds.ad2 || !activeAds.ad2.container) {
            return false;
        }
        
        // Check if Ad 2 is already in the DOM
        if (activeAds.ad2.container.parentNode) {
            return false;
        }
        
        // Find the answer block
        var answerBlocks = document.querySelectorAll('.ai-search-answer.active');
        if (answerBlocks.length > 0) {
            var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
            lastAnswerBlock.appendChild(activeAds.ad2.container);
            return true;
        }
        
        return false;
    } catch (error) {
        console.error('[AD] Error inserting Ad 2 into answer block:', error);
        return false;
    }
}

/**
 * Gets the current ad configuration
 */
function getAdConfig() {
    return AD_CONFIG;
}

module.exports = {
    showAd1OnQuery: showAd1OnQuery,
    showAd2AfterAnswer: showAd2AfterAnswer,
    insertAd2IntoAnswerBlock: insertAd2IntoAnswerBlock,
    cleanupAllAds: cleanupAllAds,
    getAdConfig: getAdConfig,
    isMobile: isMobile,
    hasValidUrlScg: hasValidUrlScg
};
